import { useMemo, useState } from 'react';
import {
  Archive,
  CheckSquare,
  Download,
  FilterX,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Mail,
  Pencil,
  Phone,
  Plus,
  RotateCcw,
  Search,
  StickyNote,
  Table as TableIcon,
  User,
  Users2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { DateRangeFilter, endOfDayParam } from '@/components/ui/date-range-filter';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { MemberSelect, useMembers } from '@/features/members';
import { CRM_OWNER_ROLES } from '@/features/crm/constants';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { BASE as ACTIVITIES_BASE } from '../api';
import { ActivityFormDialog } from '../components/ActivityFormDialog';
import { ACTIVITY_TYPES, ACTIVITY_TYPE_LABEL, type ActivityType } from '../constants';
import {
  useActivities,
  useArchiveActivity,
  useCreateActivity,
  usePurgeActivity,
  useRestoreActivity,
  useUpdateActivity,
} from '../queries';
import { useActivitiesUi, type ActivitiesViewMode } from '../store/viewPrefs';
import type { Activity, ActivityInput } from '../types';

const ALL = 'all';
const TABLE_COLS = 6;

/** Columns the backend lets us order by — keeps typos out of the URL. */
type ActivityOrderingColumn = 'subject' | 'type' | 'occurred_at' | 'created' | 'modified';

const TYPE_ICON: Record<ActivityType, LucideIcon> = {
  call: Phone,
  meeting: Users2,
  email: Mail,
  note: StickyNote,
  task: CheckSquare,
};

const VIEW_MODES: readonly ViewModeOption<ActivitiesViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by type', icon: Layers },
];

function relatedLabel(a: Activity): string {
  const parts: string[] = [];
  if (a.deal) parts.push('Deal');
  if (a.company) parts.push('Company');
  if (a.contact) parts.push('Contact');
  return parts.join(' · ');
}

function whenLabel(iso: string): string {
  // No shared date helper yet — use the platform locale formatter.
  return new Date(iso).toLocaleString();
}

function notesExcerpt(notes: string, max = 140): string {
  const trimmed = notes.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

export function ActivitiesListPage() {
  const view = useActivitiesUi((s) => s.view);
  const setView = useActivitiesUi((s) => s.setView);
  const showArchived = useActivitiesUi((s) => s.showArchived);
  const setShowArchived = useActivitiesUi((s) => s.setShowArchived);
  const ordering = useActivitiesUi((s) => s.ordering);
  const setOrdering = useActivitiesUi((s) => s.setOrdering);
  const mine = useActivitiesUi((s) => s.mine);
  const setMine = useActivitiesUi((s) => s.setMine);
  // Persisted filters (survive reloads via the store). `occurred_at` is a
  // DateTime, so the `to` bound expands to end-of-day to keep the day inclusive.
  const typeFilter = useActivitiesUi((s) => s.type);
  const setTypeFilter = useActivitiesUi((s) => s.setType);
  const owner = useActivitiesUi((s) => s.owner);
  const setOwner = useActivitiesUi((s) => s.setOwner);
  const occurredFrom = useActivitiesUi((s) => s.occurredFrom);
  const setOccurredFrom = useActivitiesUi((s) => s.setOccurredFrom);
  const occurredTo = useActivitiesUi((s) => s.occurredTo);
  const setOccurredTo = useActivitiesUi((s) => s.setOccurredTo);

  const [searchInput, setSearchInput] = useState('');
  // Live, debounced search — fires 300ms after typing stops (no Search button).
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Activity | undefined>(undefined);
  const [toArchive, setToArchive] = useState<Activity | null>(null);

  const filtersActive =
    searchInput !== '' ||
    typeFilter !== ALL ||
    owner !== '' ||
    occurredFrom !== '' ||
    occurredTo !== '' ||
    mine ||
    showArchived;

  function resetFilters() {
    setSearchInput('');
    setTypeFilter(ALL);
    setOwner('');
    setOccurredFrom('');
    setOccurredTo('');
    setMine(false);
    setShowArchived(false);
  }

  const { data, isPending, isError, error, isFetching } = useActivities({
    search: search || undefined,
    type: typeFilter === ALL ? undefined : typeFilter,
    archived: showArchived ? true : undefined,
    ordering,
    // Send `mine: true` only when active — backend reads the user.
    mine: mine ? true : undefined,
    owner: owner ? Number(owner) : undefined,
    occurred_at_after: occurredFrom || undefined,
    occurred_at_before: endOfDayParam(occurredTo),
  });
  const { data: membersData } = useMembers();
  const ownerNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of membersData?.results ?? []) map.set(m.user.id, m.user.full_name);
    return map;
  }, [membersData]);

  const create = useCreateActivity();
  const update = useUpdateActivity();
  const archive = useArchiveActivity();
  const restore = useRestoreActivity();
  const purge = usePurgeActivity();

  const activities = data?.results ?? [];

  async function handleSubmit(input: ActivityInput) {
    if (editing) await update.mutateAsync({ id: editing.id, input });
    else await create.mutateAsync(input);
  }

  function openEdit(a: Activity) {
    setEditing(a);
    setDialogOpen(true);
  }

  function ownerName(a: Activity): string {
    if (!a.owner) return '—';
    return ownerNames.get(a.owner) ?? '…';
  }

  // Empty state is consistent across views: keep the table chrome with an
  // inline empty row regardless of the persisted view mode, so the page never
  // collapses into a tiny floating message.
  const showEmptyTable = !isPending && !isError && activities.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Activities</h1>
        <Button
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
        >
          <Plus className="size-4" /> Log activity
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-56">
            <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
            <Input
              className="pl-8"
              placeholder="Search activities"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search activities"
            />
          </div>
          <Select
            value={typeFilter}
            onValueChange={(v) => setTypeFilter(v as ActivityType | typeof ALL)}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              {ACTIVITY_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <MemberSelect
            value={owner}
            onChange={setOwner}
            placeholder="All sales"
            queryKey="activities-owner-filter"
            roles={CRM_OWNER_ROLES}
            clearable
            triggerClassName="h-9 w-auto min-w-28"
          />
          <DateRangeFilter
            label="Occurred"
            idPrefix="activity-occurred"
            from={occurredFrom}
            to={occurredTo}
            onFromChange={setOccurredFrom}
            onToChange={setOccurredTo}
          />
          {filtersActive ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={resetFilters}
              aria-label="Reset all filters"
              title="Reset all filters"
            >
              <FilterX className="size-4" />
            </Button>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={mine ? 'default' : 'outline'}
            onClick={() => setMine(!mine)}
            aria-pressed={mine}
            aria-label={mine ? 'Showing my activities' : 'Show only my activities'}
          >
            <User className="size-4" /> Mine
          </Button>
          <Button
            type="button"
            size="icon"
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => setShowArchived(!showArchived)}
            aria-pressed={showArchived}
            aria-label={
              showArchived ? 'Showing archived activities' : 'Show archived activities'
            }
            title={showArchived ? 'Showing archived activities' : 'Show archived activities'}
          >
            <Archive className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              // Mirror the live list filters and add `export=csv`; the backend
              // returns `Content-Disposition: attachment`, so this navigation
              // downloads `activities.csv` without leaving the SPA.
              void exportCsv(ACTIVITIES_BASE, {
                search: search || undefined,
                type: typeFilter === ALL ? undefined : typeFilter,
                owner: owner ? Number(owner) : undefined,
                archived: showArchived ? true : undefined,
                ordering,
                mine: mine ? true : undefined,
                occurred_at_after: occurredFrom || undefined,
                occurred_at_before: endOfDayParam(occurredTo),
                export: 'csv',
              });
            }}
            aria-label="Export activities as CSV"
            title="Export activities as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">
          {(error as unknown as ApiError)?.message ?? 'Failed to load activities.'}
        </p>
      ) : isPending ? (
        <ActivitiesTable
          activities={[]}
          isPending
          ownerName={ownerName}
          onEdit={openEdit}
          onArchive={setToArchive}
          onRestore={(a) => restore.mutate(a.id)}
          onPurge={(a) => purge.mutate(a.id)}
          aria-busy={isFetching}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : showEmptyTable ? (
        <ActivitiesTable
          activities={[]}
          ownerName={ownerName}
          onEdit={openEdit}
          onArchive={setToArchive}
          onRestore={(a) => restore.mutate(a.id)}
          onPurge={(a) => purge.mutate(a.id)}
          emptyMessage={showArchived ? 'No archived activities.' : undefined}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'table' ? (
        <ActivitiesTable
          activities={activities}
          ownerName={ownerName}
          onEdit={openEdit}
          onArchive={setToArchive}
          onRestore={(a) => restore.mutate(a.id)}
          onPurge={(a) => purge.mutate(a.id)}
          aria-busy={isFetching}
          ordering={ordering}
          onOrderingChange={setOrdering}
        />
      ) : view === 'list' ? (
        <ActivitiesList
          activities={activities}
          ownerName={ownerName}
          onOpen={openEdit}
        />
      ) : view === 'cards' ? (
        <ActivitiesCards
          activities={activities}
          ownerName={ownerName}
          onOpen={openEdit}
        />
      ) : (
        <ActivitiesGroupedByType
          activities={activities}
          ownerName={ownerName}
          onOpen={openEdit}
        />
      )}

      <ActivityFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        activity={editing}
        onSubmit={handleSubmit}
      />

      <AlertDialog open={Boolean(toArchive)} onOpenChange={(o) => !o && setToArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this activity?</AlertDialogTitle>
            <AlertDialogDescription>
              Hide “{toArchive?.subject}” from the active list. You can restore it
              later from Archived view.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toArchive) archive.mutate(toArchive.id);
                setToArchive(null);
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ViewProps {
  activities: Activity[];
  ownerName: (a: Activity) => string;
  onOpen: (a: Activity) => void;
}

interface TableProps {
  activities: Activity[];
  isPending?: boolean;
  ownerName: (a: Activity) => string;
  onEdit: (a: Activity) => void;
  onArchive: (a: Activity) => void;
  onRestore: (a: Activity) => void;
  onPurge: (a: Activity) => void;
  emptyMessage?: string;
  'aria-busy'?: boolean;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function ActivitiesTable({
  activities,
  isPending = false,
  ownerName,
  onEdit,
  onArchive,
  onRestore,
  onPurge,
  emptyMessage,
  'aria-busy': ariaBusy,
  ordering,
  onOrderingChange,
}: TableProps) {
  return (
    <div className="rounded-md border" aria-busy={ariaBusy}>
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<ActivityOrderingColumn>
              column="type"
              value={ordering}
              onChange={onOrderingChange}
            >
              Type
            </SortableTableHead>
            <SortableTableHead<ActivityOrderingColumn>
              column="subject"
              value={ordering}
              onChange={onOrderingChange}
            >
              Subject
            </SortableTableHead>
            <SortableTableHead<ActivityOrderingColumn>
              column="occurred_at"
              value={ordering}
              onChange={onOrderingChange}
            >
              When
            </SortableTableHead>
            <TableHead>Related</TableHead>
            <TableHead>Owner</TableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isPending ? (
            Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={TABLE_COLS}>
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              </TableRow>
            ))
          ) : activities.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={TABLE_COLS}
                className="text-muted-foreground py-10 text-center"
              >
                {emptyMessage ?? 'No activities logged yet.'}
              </TableCell>
            </TableRow>
          ) : (
            activities.map((a) => {
              const Icon = TYPE_ICON[a.type];
              return (
                <TableRow key={a.id}>
                  <TableCell>
                    <span className="flex items-center gap-2">
                      <Icon className="text-muted-foreground size-4" />
                      {ACTIVITY_TYPE_LABEL[a.type]}
                    </span>
                  </TableCell>
                  <TableCell className="font-medium">{a.subject}</TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap">
                    {whenLabel(a.occurred_at)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {relatedLabel(a) ? (
                      <Badge variant="outline">{relatedLabel(a)}</Badge>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{ownerName(a)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Edit ${a.subject}`}
                      onClick={() => onEdit(a)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    {a.is_archived ? (
                      <span className="inline-flex items-center justify-end">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Restore ${a.subject}`}
                          onClick={() => onRestore(a)}
                        >
                          <RotateCcw className="size-4" />
                        </Button>
                        <PurgeArchivedButton
                          name={a.subject}
                          onPurge={() => onPurge(a)}
                        />
                      </span>
                    ) : (
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Archive ${a.subject}`}
                        onClick={() => onArchive(a)}
                      >
                        <Archive className="size-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function ActivitiesList({ activities, ownerName, onOpen }: ViewProps) {
  return (
    <ul className="divide-y rounded-md border">
      {activities.map((a) => {
        const Icon = TYPE_ICON[a.type];
        return (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => onOpen(a)}
              className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
            >
              <Badge variant="outline" className="shrink-0 gap-1">
                <Icon className="size-3.5" />
                {ACTIVITY_TYPE_LABEL[a.type]}
              </Badge>
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {a.subject}
              </span>
              <span className="text-muted-foreground hidden text-xs whitespace-nowrap sm:inline">
                {whenLabel(a.occurred_at)}
              </span>
              <span className="text-muted-foreground hidden text-xs md:inline">
                {ownerName(a)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ActivitiesCards({ activities, ownerName, onOpen }: ViewProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {activities.map((a) => {
        const Icon = TYPE_ICON[a.type];
        const excerpt = notesExcerpt(a.notes);
        return (
          <Card
            key={a.id}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(a)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(a);
              }
            }}
            className="hover:bg-accent/30 cursor-pointer transition-colors"
          >
            <CardHeader>
              <CardTitle className="text-base">{a.subject}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="gap-1">
                  <Icon className="size-3.5" />
                  {ACTIVITY_TYPE_LABEL[a.type]}
                </Badge>
                <span className="text-muted-foreground">{whenLabel(a.occurred_at)}</span>
                <span className="text-muted-foreground ml-auto">{ownerName(a)}</span>
              </div>
              {excerpt ? (
                <p className="text-muted-foreground line-clamp-3">{excerpt}</p>
              ) : null}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ActivitiesGroupedByType({ activities, ownerName, onOpen }: ViewProps) {
  // Bucket by type, preserving ACTIVITY_TYPES order. Empty buckets skipped.
  const byType = useMemo(() => {
    const map = new Map<ActivityType, Activity[]>();
    for (const t of ACTIVITY_TYPES) map.set(t.value, []);
    for (const a of activities) {
      const bucket = map.get(a.type) ?? [];
      bucket.push(a);
      map.set(a.type, bucket);
    }
    return map;
  }, [activities]);

  return (
    <div className="space-y-6">
      {ACTIVITY_TYPES.map(({ value, label }) => {
        const bucket = byType.get(value) ?? [];
        if (bucket.length === 0) return null;
        return (
          <section key={value} aria-labelledby={`activity-type-${value}`}>
            <h2
              id={`activity-type-${value}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <ActivitiesCards
              activities={bucket}
              ownerName={ownerName}
              onOpen={onOpen}
            />
          </section>
        );
      })}
    </div>
  );
}
